import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Box,
  Stepper,
  Step,
  StepLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { CloudDownload as ImportIcon } from '@mui/icons-material';
import { getAwsS3Buckets, importS3Bucket } from '../api/s3';

const AWS_REGIONS = [
  'us-east-1',
  'us-east-2',
  'us-west-1',
  'us-west-2',
  'eu-west-1',
  'eu-west-2',
  'eu-central-1',
  'ap-northeast-1',
  'ap-southeast-1',
  'ap-southeast-2',
];

function S3BucketImportDialog({ open, onClose, onSuccess }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [awsBuckets, setAwsBuckets] = useState([]);
  const [selectedBucket, setSelectedBucket] = useState(null);
  const [discoveryRoleArn, setDiscoveryRoleArn] = useState('');
  const [importRoleArn, setImportRoleArn] = useState('');
  const [region, setRegion] = useState('us-east-1');
  const [description, setDescription] = useState('');

  useEffect(() => {
    if (open) {
      fetchAwsBuckets();
    } else {
      setStep(0);
      setError('');
      setAwsBuckets([]);
      setSelectedBucket(null);
      setDiscoveryRoleArn('');
      setImportRoleArn('');
      setRegion('us-east-1');
      setDescription('');
    }
  }, [open]);

  const fetchAwsBuckets = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getAwsS3Buckets(discoveryRoleArn || null);
      setAwsBuckets(data.buckets || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to list AWS S3 buckets');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBucket = (bucket) => {
    setSelectedBucket(bucket);
    setStep(1);
  };

  const handleImport = async () => {
    if (!selectedBucket) return;
    setLoading(true);
    setError('');
    try {
      await importS3Bucket({
        bucket_name: selectedBucket.name,
        region: region,
        aws_role_arn: importRoleArn || null,
        description: description || null,
      });
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to import bucket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Import Existing S3 Bucket</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3, mt: 1 }}>
          <Step><StepLabel>Select Bucket</StepLabel></Step>
          <Step><StepLabel>Configure</StepLabel></Step>
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {step === 0 && (
          <>
            <Box display="flex" gap={2} mb={2} alignItems="center">
              <TextField
                label="Role ARN (optional, for cross-account)"
                value={discoveryRoleArn}
                onChange={(e) => setDiscoveryRoleArn(e.target.value)}
                size="small"
                fullWidth
                placeholder="arn:aws:iam::123456789012:role/..."
              />
              <Button variant="outlined" onClick={fetchAwsBuckets} disabled={loading}>
                {loading ? <CircularProgress size={20} /> : 'Refresh'}
              </Button>
            </Box>

            {loading ? (
              <Box textAlign="center" py={4}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Bucket Name</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {awsBuckets.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography color="textSecondary" py={2}>
                            No S3 buckets found in this AWS account.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      awsBuckets.map((bucket) => (
                        <TableRow key={bucket.name}>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{bucket.name}</TableCell>
                          <TableCell>
                            {bucket.creation_date
                              ? new Date(bucket.creation_date).toLocaleDateString()
                              : '-'}
                          </TableCell>
                          <TableCell>
                            {bucket.already_imported ? (
                              <Chip label="Already Imported" size="small" color="default" />
                            ) : (
                              <Chip label="Available" size="small" color="success" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={bucket.already_imported}
                              onClick={() => handleSelectBucket(bucket)}
                              startIcon={<ImportIcon />}
                            >
                              Import
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}

        {step === 1 && selectedBucket && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Bucket Name"
              value={selectedBucket.name}
              disabled
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>AWS Region</InputLabel>
              <Select
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                label="AWS Region"
              >
                {AWS_REGIONS.map(r => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="AWS Role ARN"
              value={importRoleArn}
              onChange={(e) => setImportRoleArn(e.target.value)}
              fullWidth
              placeholder="arn:aws:iam::123456789012:role/S3AccessRole"
              helperText="Optional: IAM role ARN for cross-account access"
            />

            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
              placeholder="Optional description for this bucket"
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 1 && (
          <Button onClick={() => setStep(0)} disabled={loading}>
            Back
          </Button>
        )}
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        {step === 1 && (
          <Button
            variant="contained"
            onClick={handleImport}
            disabled={loading || !selectedBucket}
          >
            {loading ? 'Importing...' : 'Import Bucket'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default S3BucketImportDialog;
